import { useQuery } from '@tanstack/react-query';
import queryString from 'query-string';
import { generatePath } from 'react-router-dom';

import { 
  API_SHARED_APP_LIST_APP_CONTENTS_URL,
  API_SHARED_APP_LIST_EDIT_URL, 
  API_SHARED_APP_LIST_URL
} from '../constants/apiUrls';
import { camelCaseKeys } from '../util/object';
import { getRequestJSON } from '../util/services';
import { useConfig } from './useConfig';

/**
 * Represents an individual app identifier in an shared app list
 */
export interface AppListContent {
  id: number;
  appIdentifier: string;
  sharedAppListId: number;
}

/**
 * Represents a label for a shared app list
 */
export interface Label {
  id: number;
  entry: string;
  type: string;
}

/**
 * Represents a complete shared app list with its app identifiers, targeting profiles, and labels
 */
export interface SharedAppListWithContents {
  id: number;
  name: string;
  description: string;
  listType: string;
  targetingProfiles: number[];
  appListContents: AppListContent[];
  labels: Label[];
}

/**
 * Hook to fetch a paginated list of shared app lists
 * @param filter Optional name filter
 * @param page Page number (1-based)
 * @param perPage Number of items per page
 * @param labelFilters Optional label filters object
 */
export function useSharedAppLists(
  filter: string = '', 
  page: number = 1, 
  perPage: number = 10, 
  labelFilters: {[key: string]: string[]} = {}
) {
  const { apiHost } = useConfig();

  return useQuery({
    queryKey: ['sharedAppLists', filter, page, perPage, labelFilters],
    queryFn: () => {
      const params: any = {
        name_filter: filter,
        page: page.toString(),
        size: perPage.toString()
      };

      // Add label filters to the query parameters
      Object.entries(labelFilters).forEach(([type, entries]) => {
        if (entries.length > 0) {
          params[`label_${type}`] = entries.join(',');
        }
      });

      return getRequestJSON(
        apiHost, 
        generatePath(API_SHARED_APP_LIST_URL, {
          '*': `?${queryString.stringify(params)}`
        })
      );
    }
  });
}

/**
 * Hook to fetch a single shared app list with its app identifiers and targeting profiles
 * @param sharedAppListId ID of the shared app list to fetch
 */
export function useSharedAppListWithContents(sharedAppListId: string | undefined) {
  const { apiHost } = useConfig();
  
  return useQuery({
    queryKey: ['sharedAppListWithContents', sharedAppListId],
    queryFn: () => {
      if (!sharedAppListId) {
        return { model: null };
      }
      
      return getRequestJSON(
        apiHost,
        generatePath(API_SHARED_APP_LIST_EDIT_URL, { id: sharedAppListId })
      );
    },
    select: camelCaseKeys,
    enabled: !!sharedAppListId
  });
}

/**
 * Hook to fetch just the app identifiers for an shared app list
 * @param sharedAppListId ID of the shared app list
 */
export function useAppListContents(sharedAppListId: string | undefined) {
  const { apiHost } = useConfig();
  
  return useQuery({
    queryKey: ['appListContents', sharedAppListId],
    queryFn: () => {
      if (!sharedAppListId) {
        return { collection: [] };
      }
      
      return getRequestJSON(
        apiHost,
        generatePath(API_SHARED_APP_LIST_APP_CONTENTS_URL, { 
          sharedAppListId 
        })
      );
    },
    select: camelCaseKeys,
    enabled: !!sharedAppListId
  });
}

/**
 * Hook to fetch multiple shared app lists by their IDs
 * @param ids Array of shared app list IDs to fetch
 */
export function useSharedAppListsByIds(ids: string[]) {
  const { apiHost } = useConfig();
  
  return useQuery({
    queryKey: ['sharedAppListsByIds', ids],
    queryFn: async () => {
      if (!ids || ids.length === 0) {
        return { collection: [] };
      }
      
      const promises = ids.map(id => 
        getRequestJSON(
          apiHost,
          generatePath(API_SHARED_APP_LIST_EDIT_URL, { id })
        ).then(response => response?.model).catch(() => null)
      );
      
      const results = await Promise.all(promises);
      return { collection: results.filter(Boolean) };
    },
    select: camelCaseKeys,
    enabled: ids && ids.length > 0
  });
}

export default useSharedAppLists;
